import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from './context/ThemeContext';
import { CloudflareAuthProvider } from './contexts/CloudflareAuthContext';
import { Toaster } from 'react-hot-toast';
import { toastConfig } from './config/toast';
import Header from './components/Header';
import Footer from './components/Footer';
import Home from './pages/Home';
import About from './pages/About';
import Contact from './pages/Contact';
import District from './pages/District';
import City from './pages/City';
import Download from './pages/Download/Download';
import Events from './pages/Events';
import EventDetails from './pages/Events/EventDetails';
import ForgotPassword from './pages/ForgotPassword';
import Login from './pages/Login';
import National from './pages/National';
import CentralZone from './pages/CentralZone';
import EastZone from './pages/EastZone';
import WestZone from './pages/WestZone';
import NorthZone from './pages/NorthZone';
import SouthZone from './pages/SouthZone';
import Photo from './pages/Photo';
import Register from './pages/Register';
import Search from './pages/Search';
import MemberVerification from './pages/MemberVerification';
import State from './pages/State';
import Woman from './pages/Woman';
import Youth from './pages/Youth';
import Range from './pages/Range';
import NRI from './pages/NRI';
import FireSafety from './pages/FireSafety';
import Video from './pages/Video';
import Dashboard from './pages/Dashboard';
import Articles from './pages/Dashboard/Articles';
import NewArticle from './pages/Dashboard/NewArticle';
import EditArticle from './pages/Dashboard/EditArticle';
import QRCode from './pages/Dashboard/QRCode';
import DashboardDownload from './pages/Dashboard/Downloads';
import CertificateDownload from './pages/Dashboard/Downloads/Certificate';
import Notifications from './pages/Dashboard/Notifications';
import Support from './pages/Dashboard/Support';
import UserDetails from './pages/Dashboard/UserDetails';
import MyMembership from './pages/Dashboard/MyMembership';
import MembershipPlans from './pages/Dashboard/MembershipPlans';
import DashboardLayout from './pages/Dashboard/Layout/DashboardLayout';
import ProtectedRoute from './components/ProtectedRoute';
import MembershipProtectedRoute from './components/MembershipProtectedRoute';
import AdminLogin from './pages/AdminLogin';
import Terms from './pages/Terms';
import FeesPolicy from './pages/FeesPolicy';
import IdCardUsagePolicy from './pages/IdCardUsagePolicy';
import PrivacyPolicy from './pages/PrivacyPolicy';
import Disclaimer from './pages/Disclaimer';
import CodeOfConduct from './pages/CodeOfConduct';
import GrievancePolicy from './pages/GrievancePolicy';
import VerifyId from './pages/VerifyId';

const App = () => {
  return (
    <Router>
      <ThemeProvider>
        <CloudflareAuthProvider>
          <div className="min-h-screen bg-white dark:bg-gray-900 transition-colors duration-300">
            <Toaster
              position="bottom-center"
              reverseOrder={false}
              gutter={8}
              toastOptions={{
                ...toastConfig,
                success: {
                  style: {
                    ...toastConfig.style,
                    background: '#064e3b', // dark green
                  },
                },
                error: {
                  style: {
                    ...toastConfig.style,
                    background: '#7f1d1d', // dark red
                  },
                  duration: 4000,
                },
              }}
            />

            <Routes>
              {/* Auth Routes */}
              <Route path="/login" element={
                <>
                  <Header />
                  <main className="pt-24">
                    <Login />
                  </main>
                </>
              } />
              <Route path="/register" element={
                <>
                  <Header />
                  <main className="pt-24">
                    <Register />
                  </main>
                </>
              } />
              <Route path="/forgot-password" element={
                <>
                  <Header />
                  <main className="pt-24">
                    <ForgotPassword />
                  </main>
                </>
              } />
              <Route path="/verify-email" element={<Navigate to="/forgot-password" replace />} />
              <Route path="/auth/action" element={<Navigate to="/forgot-password" replace />} />
              <Route path="/admin-login" element={<AdminLogin />} />

              {/* Dashboard Routes */}
              <Route path="/dashboard" element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}>
                <Route index element={<Dashboard />} />
                <Route path="articles" element={
                  <MembershipProtectedRoute>
                    <Articles />
                  </MembershipProtectedRoute>
                } />
                <Route path="new-article" element={
                  <MembershipProtectedRoute>
                    <NewArticle />
                  </MembershipProtectedRoute>
                } />
                <Route path="edit-article/:id" element={
                  <MembershipProtectedRoute>
                    <EditArticle />
                  </MembershipProtectedRoute>
                } />
                <Route path="qr-code" element={
                  <MembershipProtectedRoute>
                    <QRCode />
                  </MembershipProtectedRoute>
                } />
                <Route path="download" element={<Navigate to="download/card" replace />} />
                <Route path="download/card" element={
                  <MembershipProtectedRoute>
                    <DashboardDownload />
                  </MembershipProtectedRoute>
                } />
                <Route path="download/certificate" element={
                  <MembershipProtectedRoute>
                    <CertificateDownload />
                  </MembershipProtectedRoute>
                } />
                <Route path="notifications" element={
                  <MembershipProtectedRoute>
                    <Notifications />
                  </MembershipProtectedRoute>
                } />
                <Route path="support" element={<Support />} />
                <Route path="membership" element={<MembershipPlans />} />
                <Route path="user-details" element={<UserDetails />} />
                <Route path="my-membership" element={<MyMembership />} />
              </Route>

              {/* Public Routes with Header and Footer */}
              <Route path="/" element={
                <>
                  <Header />
                  <main className="pt-24">
                    <Home />
                  </main>
                  <Footer />
                </>
              } />
              <Route path="/about" element={
                <>
                  <Header />
                  <main className="pt-24">
                    <About />
                  </main>
                  <Footer />
                </>
              } />
              <Route path="/contact" element={
                <>
                  <Header />
                  <main className="pt-24">
                    <Contact />
                  </main>
                  <Footer />
                </>
              } />
              <Route path="/district" element={
                <>
                  <Header />
                  <main className="pt-24">
                    <District />
                  </main>
                  <Footer />
                </>
              } />
              <Route path="/city" element={
                <>
                  <Header />
                  <main className="pt-24">
                    <City />
                  </main>
                  <Footer />
                </>
              } />
              <Route path="/download" element={
                <>
                  <Header />
                  <main className="pt-24">
                    <Download />
                  </main>
                  <Footer />
                </>
              } />
              <Route path="/events" element={
                <>
                  <Header />
                  <main className="pt-24">
                    <Events />
                  </main>
                  <Footer />
                </>
              } />
              <Route path="/events/:id" element={
                <>
                  <Header />
                  <main className="pt-24">
                    <EventDetails />
                  </main>
                  <Footer />
                </>
              } />
              <Route path="/national" element={
                <>
                  <Header />
                  <main className="pt-24">
                    <National />
                  </main>
                  <Footer />
                </>
              } />
              <Route path="/central-zone" element={
                <>
                  <Header />
                  <main className="pt-24">
                    <CentralZone />
                  </main>
                  <Footer />
                </>
              } />
              <Route path="/east-zone" element={
                <>
                  <Header />
                  <main className="pt-24">
                    <EastZone />
                  </main>
                  <Footer />
                </>
              } />
              <Route path="/west-zone" element={
                <>
                  <Header />
                  <main className="pt-24">
                    <WestZone />
                  </main>
                  <Footer />
                </>
              } />
              <Route path="/north-zone" element={
                <>
                  <Header />
                  <main className="pt-24">
                    <NorthZone />
                  </main>
                  <Footer />
                </>
              } />
              <Route path="/south-zone" element={
                <>
                  <Header />
                  <main className="pt-24">
                    <SouthZone />
                  </main>
                  <Footer />
                </>
              } />
              <Route path="/photo" element={
                <>
                  <Header />
                  <main className="pt-24">
                    <Photo />
                  </main>
                  <Footer />
                </>
              } />
              <Route path="/search" element={
                <>
                  <Header />
                  <main className="pt-24">
                    <Search />
                  </main>
                  <Footer />
                </>
              } />
              <Route path="/member/:enrollmentNumber" element={
                <MemberVerification />
              } />
              <Route path="/state" element={
                <>
                  <Header />
                  <main className="pt-24">
                    <State />
                  </main>
                  <Footer />
                </>
              } />
              <Route path="/woman" element={
                <>
                  <Header />
                  <main className="pt-24">
                    <Woman />
                  </main>
                  <Footer />
                </>
              } />
              <Route path="/youth" element={
                <>
                  <Header />
                  <main className="pt-24">
                    <Youth />
                  </main>
                  <Footer />
                </>
              } />
              <Route path="/range" element={
                <>
                  <Header />
                  <main className="pt-24">
                    <Range />
                  </main>
                  <Footer />
                </>
              } />
              <Route path="/nri" element={
                <>
                  <Header />
                  <main className="pt-24">
                    <NRI />
                  </main>
                  <Footer />
                </>
              } />
              <Route path="/fire-safety" element={
                <>
                  <Header />
                  <main className="pt-24">
                    <FireSafety />
                  </main>
                  <Footer />
                </>
              } />
              <Route path="/video" element={
                <>
                  <Header />
                  <main className="pt-24">
                    <Video />
                  </main>
                  <Footer />
                </>
              } />

              {/* Policy / Legal Pages */}
              <Route path="/terms" element={
                <>
                  <Header />
                  <main className="pt-24">
                    <Terms />
                  </main>
                  <Footer />
                </>
              } />
              <Route path="/fees-policy" element={
                <>
                  <Header />
                  <main className="pt-24">
                    <FeesPolicy />
                  </main>
                  <Footer />
                </>
              } />
              <Route path="/id-card-usage-policy" element={
                <>
                  <Header />
                  <main className="pt-24">
                    <IdCardUsagePolicy />
                  </main>
                  <Footer />
                </>
              } />
              <Route path="/privacy-policy" element={
                <>
                  <Header />
                  <main className="pt-24">
                    <PrivacyPolicy />
                  </main>
                  <Footer />
                </>
              } />
              <Route path="/disclaimer" element={
                <>
                  <Header />
                  <main className="pt-24">
                    <Disclaimer />
                  </main>
                  <Footer />
                </>
              } />
              <Route path="/code-of-conduct" element={
                <>
                  <Header />
                  <main className="pt-24">
                    <CodeOfConduct />
                  </main>
                  <Footer />
                </>
              } />
              <Route path="/grievance-policy" element={
                <>
                  <Header />
                  <main className="pt-24">
                    <GrievancePolicy />
                  </main>
                  <Footer />
                </>
              } />
              <Route path="/verify-id" element={
                <>
                  <Header />
                  <main className="pt-24">
                    <VerifyId />
                  </main>
                  <Footer />
                </>
              } />
            </Routes>
          </div>
        </CloudflareAuthProvider>
      </ThemeProvider>
    </Router>
  );
};

export default App;
